<template>
  <v-dialog :value="true" persistent :max-width="isDelete ? 570 : 1000">
    <v-card>
      <v-card-title class="justify-center">
        <v-row justify="center">
          <v-col cols="1"> </v-col>
          <v-col class="d-flex justify-center" cols="10">
            {{
              isDisplay
                ? externalAuth.name
                : $t(`externalAuth.actions.title.${action}`)
            }}
          </v-col>
          <v-col cols="1" class="d-flex justify-end">
            <v-tooltip bottom v-if="isAdminUser">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="isDisplay"
                  :disabled="loading"
                  v-bind="attrs"
                  v-on="on"
                  class="primary secondary--text"
                  fab
                  small
                  @click="clickExternalAuthInfo"
                  id="edit-externalAuth"
                >
                  <v-icon v-if="userIsEditing"> mdi-close </v-icon>
                  <v-icon v-else> mdi-pencil </v-icon>
                </v-btn>
              </template>
              <div>
                {{
                  userIsEditing
                    ? $t('cancelUpdate')
                    : $t('externalAuth.actions.title.update')
                }}
              </div>
            </v-tooltip>
            <v-btn
              v-else
              icon
              small
              @click="closeDialog"
              id="close-externalAuth-dialog"
            >
              <v-icon> mdi-close </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text v-if="isDelete" class="body-1">
        <v-col align="center">
          <h2 class="mt-1">{{ externalAuth.name }}</h2>
          <span>{{ $t('externalAuth.delete') }}</span>
        </v-col>
      </v-card-text>

      <v-card-text v-else-if="isDisplay || isCreate || isUpdate">
        <v-form
          ref="externalAuthForm"
          v-model="validExternalAuthForm"
          lazy-validation
          @submit.prevent="onSubmit"
        >
          <v-row class="mt-4" align="baseline">
            <v-col cols="12">
              <v-text-field
                :disabled="loading || isDisplayAndUserIsNotEditing"
                :label="$t('externalAuth.name')"
                prepend-icon="mdi-account"
                :value="name"
                @input="name = $event"
                :rules="[
                  notEmpty,
                  maxCharacters(maxChars),
                  isCreate
                    ? unique(externalAuthNames, 'externalAuthNameAlreadyExists')
                    : unique(
                        externalAuthNamesWithoutCurrent,
                        'externalAuthNameAlreadyExists'
                      ),
                ]"
                dense
                autocomplete="false"
                id="externalAuth-name-input"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                :disabled="true"
                :label="$t('externalAuth.type')"
                prepend-icon="mdi-domain"
                :value="type"
                @input="type = $event"
                :rules="[notEmpty]"
                dense
                autocomplete="false"
                id="externalAuth-authority-type"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                :disabled="loading || isDisplayAndUserIsNotEditing"
                :label="$t('externalAuth.authority')"
                prepend-icon="mdi-access-point"
                :value="authority"
                @input="authority = $event"
                :rules="[notEmpty]"
                dense
                autocomplete="false"
                id="externalAuth-authority-input"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                :disabled="loading || isDisplayAndUserIsNotEditing"
                :label="$t('externalAuth.clientId')"
                prepend-icon="mdi-identifier"
                :value="clientId"
                @input="clientId = $event"
                :rules="[notEmpty]"
                dense
                autocomplete="false"
                id="externalAuth-clientId-input"
              />
            </v-col>
            <!-- <v-col cols="12">
              <v-switch
                :disabled="loading || isDisplayAndUserIsNotEditing"
                v-model="calendarEnabled"
                :label="$t('externalAuth.calendarEnabled')"
                prepend-icon="mdi-calendar-check"
                id="externalAuth-calendarEnabled-switch"
                dense
              />
            </v-col> -->
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="headline justify-center" v-if="isAdminUser">
        <v-btn
          :disabled="loading"
          color="error"
          rounded
          class="white--text"
          :width="!isMobile ? '150px' : '100px'"
          @click="closeDialog"
          id="close-externalAuth-dialog"
        >
          {{ $t('close') }}
        </v-btn>
        <v-btn
          rounded
          color="accept"
          class="white--text"
          :width="!isMobile ? '150px' : '100px'"
          :loading="loading"
          :disabled="
            loading || isDisplayAndUserIsNotEditing || isExternalAuthInUse
          "
          @click="performAction"
          id="confirm-externalAuth"
        >
          {{ $t('confirm') }}
          <template v-slot:loader>
            <v-progress-circular indeterminate size="20" width="2" />
          </template>
        </v-btn>
      </v-card-actions>

      <div
        class="mt-12"
        style="display: flex; justify-content: center; width: 100%"
        v-if="isDelete && isExternalAuthInUse"
      >
        <v-alert
          type="error"
          color="red"
          style="max-width: 90%"
          v-html="
            $t('externalAuth.cannotBeDeletedLine1') +
            '<br/>' +
            '<br/>' +
            $t('externalAuth.cannotBeDeletedLine2')
          "
        >
        </v-alert>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import rules from '@/utils/rules'
import { mapGetters, mapMutations } from 'vuex'
import getErrorText from '@/utils/get-error-text'
import {
  createExternalAuth,
  deleteExternalAuth,
  updateExternalAuth,
  externalAuthInUse,
} from '@/services/externals-auths-service'

export default {
  props: {
    externalAuth: Object,
    externalsAuths: Array,
    action: String,
  },
  created() {},
  mounted() {
    this.type = 'Microsoft'
    if (this.externalAuth?.id) {
      this.name = this.externalAuth.name
      this.type = this.externalAuth.type
      this.authority = this.externalAuth.authority
      this.clientId = this.externalAuth.clientId
      this.calendarEnabled = this.externalAuth.calendarEnabled ?? false
    }
    this.configured = true
  },
  data() {
    return {
      validExternalAuthForm: false,
      maxChars: 100,
      loading: false,
      userIsEditing: false,
      name: undefined,
      type: undefined,
      authority: undefined,
      clientId: undefined,
      calendarEnabled: false,
      configured: false,
      isExternalAuthInUse: false,
    }
  },
  methods: {
    ...mapMutations(['setSnackbar']),
    ...rules,
    /**
     * Shortens the text.
     * @param {String} text Text to shorten.
     * @param {Number} size Size of the text.
     */
    getShortText(text, size = 12) {
      return text.length > size ? text.substring(0, size - 3) + '...' : text
    },
    closeDialog() {
      this.$emit('closeDialog')
    },
    /**
     * When the pencil button is clicked, the user is allowed to edit.
     */
    clickExternalAuthInfo() {
      this.userIsEditing = !this.userIsEditing
    },
    /**
     * Performs the action of the dialog (create, update or delete).
     */
    async performAction() {
      if (!this.isDelete && !this.$refs.externalAuthForm?.validate()) return

      this.loading = true
      try {
        const message = this.$t('externalAuth.actions.success.' + this.action)

        if (this.isCreate) {
          await createExternalAuth({
            name: this.name,
            type: this.type,
            authority: this.authority,
            clientId: this.clientId,
            calendarEnabled: this.calendarEnabled,
          })
        } else if (this.isUpdate || this.isDisplay) {
          await updateExternalAuth(this.externalAuth.id, {
            name: this.name,
            type: this.type,
            authority: this.authority,
            clientId: this.clientId,
            calendarEnabled: this.calendarEnabled,
          })
        } else if (this.isDelete) {
          const deleted = await this.deleteExternalAuth(this.externalAuth.id)
          if (!deleted) {
            return
          }
        }

        this.setSnackbar({ position: 'top', type: 'success', message })
        this.closeDialog()
      } catch (error) {
        const message = getErrorText(error.message)
        this.setSnackbar({
          position: 'top',
          type: 'error',
          message,
        })
      } finally {
        this.loading = false
      }
    },
    /**
     * Given a list of objects with names, it returns a new list of just names.
     * @param {Array} list List that contains objects with names.
     * @returns Array with the names of the given list.
     */
    getNames(list = []) {
      return list.map((item) => item.name)
    },
    async deleteExternalAuth(externalAuthId) {
      this.isExternalAuthInUse = await externalAuthInUse(externalAuthId)
      if (this.isExternalAuthInUse) {
        return false
      }
      await deleteExternalAuth(externalAuthId)
      return true
    },
  },
  computed: {
    ...mapGetters(['isMobile', 'isAdminUser']),
    isCreate() {
      return this.action === 'create'
    },
    isUpdate() {
      return this.action === 'update'
    },
    isDelete() {
      return this.action === 'delete'
    },
    isDisplay() {
      return this.action === 'display'
    },
    isDisplayAndUserIsNotEditing() {
      return this.action === 'display' && !this.userIsEditing
    },
    externalAuthNames() {
      return this.externalsAuths?.map((m) => m.name) ?? []
    },
    externalAuthNamesWithoutCurrent() {
      return (
        this.externalsAuths
          ?.map((m) => m.name)
          .filter((name) => name !== this.externalAuth.name) ?? []
      )
    },
  },
}
</script>

<style>
@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.appear-anim {
  animation-name: fade;
  animation-duration: 0.4s;
}
</style>
